import React, { FC, useEffect } from 'react';
import { BuilderRenderer } from '@fiverr-private/content_pages_sdk';
import { Currency } from '@fiverr-private/currencies_plugin';
import { addLinkQueryParams } from '../../../../server/utils/addLinkQueryParams';
import { guideViewed } from '../../../../utils/bi';
import { logger } from '../../../../server/libs/logger';
import { ArticleData } from '../../../../shared/types';
import { RootContentTypes } from '../../../constants';
import { Image } from '../../shared/Image';
import { ArticleHeader } from './components/articleHeader/articleHeader';
import RelatedGuides from './components/relatedGuides/RelatedGuides';
import { AboutAuthor } from './components/aboutAuthor/AboutAuthor';
import { Faqs } from './components/faqs/Faqs';

import './index.scss';

const responsiveImageData = {
  sizes: ['(min-width: 600px) 800px', '(min-width: 480px) 560px', '440px'],
  srcSet: [800, 560, 440, 1600, 880], // 1600 and 1600 used for retina
};

interface GuideArticleProps {
  data: ArticleData;
  currencyObj: Currency;
  builderPublicKey: string;
}

const GuideArticle: FC<GuideArticleProps> = (props) => {
  const { data: article, currencyObj, builderPublicKey } = props;
  const {
    title,
    description,
    publishedDate,
    dateModified,
    timeToRead,
    author,
    category: {
      value: { data: category },
    },
    relatedArticles,
    introduction,
    mainImage,
    faqs,
  } = article.data;

  useEffect(() => {
    guideViewed({ guide: { name: title }, category: { name: category.title } });
  }, [category.title, title]);

  return (
    <div className="guide-article">
      <ArticleHeader
        title={title}
        description={description}
        publishedDate={publishedDate}
        dateModified={dateModified}
        timeToRead={timeToRead}
        category={category}
        author={{ authorName: author?.value.data.authorName || '' }}
      />

      <div className="main-img">
        <Image srcset={[mainImage.image]} alt={mainImage.alt} responsiveImageData={responsiveImageData} lazyLoad />
      </div>

      <div className="main-content">
        {introduction && (
          <section className="article-intro" dangerouslySetInnerHTML={{ __html: addLinkQueryParams(introduction) }} />
        )}
        <BuilderRenderer
          builderApiKey={builderPublicKey}
          modelName={RootContentTypes.ARTICLE}
          section={article}
          reportingManagerContext={{
            page: {
              name: title,
            },
            publisher: {
              subDomain: 'fiverr',
            },
          }}
          state={{
            contextData: {
              currencyObject: currencyObj,
            },
          }}
          context={{}}
          onError={(error, context) => {
            logger.error(context.description, {
              ...context,
              error,
            });
          }}
        />

        <Faqs faqs={faqs} />
        {relatedArticles && relatedArticles.length > 0 && <RelatedGuides relatedGuides={relatedArticles} />}
        {author && <AboutAuthor {...author.value.data} />}
      </div>
    </div>
  );
};

export default GuideArticle;
